/**
 * Utility functions for energy-related calculations
 * 
 * This module contains utility functions for calculating various energy-related metrics
 * including power flow, energy mix, feed-in rate adjustments, and battery lifespan.
 * These functions provide the core calculations needed for the Energy Flow tab and
 * power-related aspects of the financial analysis.
 */
import { 
  Config,
  OperatingScenario 
} from '../types/config';
import { 
  DailyPowerFlowItem,
  EnergyMixItem,
  FeedInCalculationParams 
} from '../types/calculations';

/**
 * Calculate the effective feed-in rate with regulatory and seasonal limitations
 * 
 * This function adjusts the nominal feed-in tariff to account for various limitations:
 * - Winter months when feed-in may be restricted or disallowed
 * - Grid congestion periods when maximum load limits prevent export
 * - Scheduled maintenance periods when grid connection is unavailable
 * 
 * The effective rate represents the actual revenue per kWh exported over a year,
 * accounting for these limitations.
 * 
 * @param params - Feed-in calculation parameters including monthly exports and limitation factors
 * @returns Effective feed-in rate in AED/kWh (lower than the nominal rate)
 */
export const calculateFeedInEffectiveRate = (params: FeedInCalculationParams): number => {
  const { 
    monthlyExports,          // Export profile by month (kWh)
    feedInRate,              // Nominal feed-in tariff (AED/kWh)
    winterMonths,            // Months with seasonal export restrictions
    maxLoadLimitFraction,    // Fraction of exports disallowed due to grid congestion
    maintenanceDowntime      // Fraction of time exports disallowed due to maintenance
  } = params;
  
  let totalExports = 0;      // Total kWh exported over the year
  let totalPaidRevenue = 0;  // Total revenue received from paid exports
  
  // Calculate exports and revenue for each month
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const exportedKwh = monthlyExports[monthIndex];
    totalExports += exportedKwh;
    
    // Check if current month is a winter month with export restrictions
    // Adding 1 because JS arrays are 0-based, but months are 1-based (Jan=1, Feb=2, etc.)
    const isWinter = winterMonths.includes(monthIndex + 1);

    // Calculate the fraction of exports that are disqualified:
    // - If winter month: 100% disqualified (no feed-in allowed)
    // - If normal month: combined percentage from grid congestion and maintenance
    let disqualifiedFraction = isWinter ? 1.0 : (maxLoadLimitFraction + maintenanceDowntime);
    
    // Ensure the disqualified fraction is between 0 and 1
    disqualifiedFraction = Math.min(1.0, Math.max(0, disqualifiedFraction));

    // Calculate fraction of exports that receive payment
    const paidFraction = 1.0 - disqualifiedFraction;
    
    // Calculate paid kWh for this month
    const paidKwh = exportedKwh * paidFraction;
    
    // Calculate revenue from paid kWh
    const revenueThisMonth = paidKwh * feedInRate;
    
    // Add to total revenue
    totalPaidRevenue += revenueThisMonth;
  }
  
  // Effective feed-in rate is total revenue divided by total exported kWh
  // Handle case where there are no exports to avoid division by zero
  if (totalExports === 0) return 0;
  
  return totalPaidRevenue / totalExports;
};

/**
 * Generate hourly power flow data for a typical day
 * 
 * This function creates a 24-hour profile of power flow from different sources
 * (solar, battery, grid) based on the system configuration. The resulting data
 * is used to visualize how energy flows through the system throughout the day.
 * 
 * The model assumes:
 * - Solar production occurs during daylight hours (9am-4pm peak)
 * - Battery discharge primarily during evening peak hours (5pm-10pm)
 * - Grid power supplements when other sources are insufficient
 * 
 * @param config - System configuration containing energy parameters
 * @param scenario - Current operating scenario ('current' or 'expanded')
 * @returns Array of hourly power flow data points for a 24-hour period
 */
export const generateDailyPowerFlow = (
  config: Config,
  scenario: OperatingScenario
): DailyPowerFlowItem[] => {
  const currentConfig = config[scenario];
  const hourlyData: DailyPowerFlowItem[] = [];
  
  // Generate data for each hour of the day
  for (let hour = 0; hour < 24; hour++) {
    // Determine if current hour is within solar production period (9am to 9am+solarHours)
    const isSolarHour = hour >= 9 && hour < 9 + currentConfig.solarHours;
    
    // Determine if current hour is within charging station operating hours
    // Either 24 hours operation or 8am to 8am+operatingHours
    const isOperatingHour = (currentConfig.operatingHours === 24) || 
                           (hour >= 8 && hour < 8 + currentConfig.operatingHours);
    
    // Initialize power values for this hour
    let solarPower = 0;
    let batteryPower = 0;
    let gridPower = 0;
    let generatorPower = 0;
    
    // Charger demand is active only during operating hours
    const chargerDemand = isOperatingHour ? currentConfig.chartCapacity : 0;
    
    // Calculate power sources during solar production hours
    if (isSolarHour) {
      // Distribute solar production evenly across solar hours (convert MWh to kW)
      solarPower = currentConfig.solarProduction * 1000 / currentConfig.solarHours;
      
      if (solarPower > chargerDemand) {
        // Solar production exceeds demand - excess goes to battery or grid export
        // For simplicity in the chart, we just show direct solar meeting demand
        batteryPower = 0;
        gridPower = 0;
      } else {
        // Solar production insufficient - supplement with grid
        // (Battery charging not shown in this hour's demand)
        batteryPower = 0;
        gridPower = chargerDemand - solarPower;
      }
    } else {
      // Non-solar hours
      if (isOperatingHour) {
        // During evening peak hours (5pm-10pm), use battery discharge
        if (hour >= 17 && hour < 22) {
          // Distribute battery capacity across 5 evening hours, limited by demand
          batteryPower = Math.min(currentConfig.batteryUsable * 1000 / 5, chargerDemand);
          // Grid supplements any remaining demand
          gridPower = chargerDemand - batteryPower;
        } else {
          // Outside evening peak, use grid power
          batteryPower = 0;
          gridPower = chargerDemand;
        }
      }
    }
    
    // For generator option comparison, always meet demand with generator during operating hours
    if (isOperatingHour) {
      generatorPower = chargerDemand;
    }
    
    // Add data point for this hour
    hourlyData.push({
      hour: `${hour}:00`,      // Format as "HH:00"
      solar: solarPower,       // Direct solar contribution (kW)
      battery: batteryPower,   // Battery discharge contribution (kW)
      grid: gridPower,         // Grid contribution (kW)
      generator: generatorPower, // Generator contribution for comparison (kW)
      demand: chargerDemand    // Total demand for this hour (kW)
    });
  }
  
  return hourlyData;
};

/**
 * Calculate energy mix data for pie chart visualization
 * 
 * This function prepares data for the energy source mix pie chart,
 * showing the proportion of energy from different sources (direct solar,
 * battery-stored solar, and grid) over a typical day.
 * 
 * @param config - System configuration containing energy parameters
 * @param scenario - Current operating scenario ('current' or 'expanded')
 * @returns Array of energy mix items with source name, value, and color
 */
export const calculateEnergyMix = (
  config: Config,
  scenario: OperatingScenario
): EnergyMixItem[] => {
  const currentConfig = config[scenario];
  
  // Create energy mix data array with name, value (MWh), and color for each source
  return [
    // Direct solar usage (solar energy used when produced)
    { 
      name: 'Direct Solar', 
      value: currentConfig.directSolarUsage, 
      color: '#FFD166'  // Yellow
    },
    
    // Battery usage (solar energy stored and used later)
    // Limited by either battery capacity or excess solar availability
    { 
      name: 'Battery', 
      value: Math.min(currentConfig.batteryUsable, currentConfig.excessSolar), 
      color: '#06D6A0'  // Green
    },
    
    // Grid electricity (purchased from utility)
    { 
      name: 'Grid', 
      value: currentConfig.gridNeededWithBattery, 
      color: '#118AB2'  // Blue
    }
  ];
};

/**
 * Calculate battery lifespan in years based on cycle life
 * 
 * This function converts the battery's rated cycle life into an expected
 * calendar lifespan in years, assuming one full cycle per day of operation.
 * 
 * @param batteryCycles - Total number of charge/discharge cycles the battery can perform
 * @returns Expected battery lifespan in years
 */
export const calculateBatteryLifespan = (batteryCycles: number): number => {
  // Assuming 1 cycle per day (365 cycles per year)
  return batteryCycles / 365;
};